import { useEffect, useMemo, useRef } from 'react'
import { useLocation } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import { useAuthStore } from '@/stores/auth'
import { bumpConsultUnreadCache, decrementConsultUnreadCache } from '@/features/consult/api'
import {
  ackChatMessage,
  getConsultChatWs,
  type WsConsultMessage,
  type WsMessageRecalledData,
} from '@/features/consult/ws'
import { useNotificationCenterStore } from '@/features/notification/notification-center-store'

function isConsultPath(pathname: string): boolean {
  return pathname === '/u/consult' || pathname === '/lawyer/consult'
}

function resolveConsultRouteRole(pathname: string): 'user' | 'lawyer' {
  return pathname.startsWith('/lawyer') ? 'lawyer' : 'user'
}

function buildConsultTarget(routeRole: 'user' | 'lawyer', sessionId: string): string {
  const base = routeRole === 'lawyer' ? '/lawyer/consult' : '/u/consult'
  return `${base}?session=${encodeURIComponent(sessionId)}`
}

function getActiveSessionId(search: string): string | null {
  return new URLSearchParams(search).get('session')
}

function isOwnMessage(message: WsConsultMessage, meId: string): boolean {
  return Boolean(meId && String(message.senderId) === meId)
}

function getMessageDedupeKey(message: WsConsultMessage): string {
  return message.id || `${message.sessionId}:${message.createTime ?? ''}`
}

function getRecallPreview(): string {
  return '该消息已撤回'
}

function getMessagePreview(message: WsConsultMessage): string {
  const content = message.content?.trim()
  if (content) return content.length > 64 ? `${content.slice(0, 64)}...` : content

  switch (Number(message.msgType)) {
    case 2:
      return '发来了一张图片'
    case 3:
      return '发来了一条语音'
    case 5:
      return message.fileName ? `发来了文件: ${message.fileName}` : '发来了一个文件'
    default:
      return '发来了一条新消息'
  }
}

function getNotificationTitle(pathname: string): string {
  return pathname.startsWith('/lawyer') ? '客户发来新消息' : '律师发来新消息'
}

function normalizeWsTimestamp(value: WsConsultMessage['createTime']): string {
  if (value == null) return new Date().toISOString()

  const raw = String(value).trim()
  if (!raw) return new Date().toISOString()

  if (/^\d+$/.test(raw)) {
    const numeric = Number(raw)
    const milliseconds = raw.length <= 10 ? numeric * 1000 : numeric
    const date = new Date(milliseconds)
    return Number.isNaN(date.getTime()) ? new Date().toISOString() : date.toISOString()
  }

  const date = new Date(raw)
  return Number.isNaN(date.getTime()) ? new Date().toISOString() : date.toISOString()
}

function showBrowserNotification(title: string, body: string, target: string) {
  if (typeof document === 'undefined' || document.visibilityState !== 'hidden') return
  if (!('Notification' in window)) return
  if (Notification.permission === 'granted') {
    const notification = new Notification(title, { body })
    notification.onclick = () => {
      window.focus()
      window.location.href = target
      notification.close()
    }
  }
}

/**
 * 登录态全局咨询消息提醒。
 *
 * 咨询页仍负责渲染消息流; 这里只处理跨页面在线提醒与标签页隐藏时的本地通知。
 */
export function ConsultMessageNotifier() {
  const location = useLocation()
  const queryClient = useQueryClient()
  const meId = String(useAuthStore((s) => s.userInfo?.userId ?? ''))
  const hasToken = useAuthStore((s) =>
    Boolean(s.accessToken && (!s.expiresAt || Date.now() < s.expiresAt)),
  )
  const addNotification = useNotificationCenterStore((s) => s.addNotification)
  const recallConsultNotification = useNotificationCenterStore((s) => s.recallConsultNotification)
  const ws = useMemo(() => getConsultChatWs(), [])
  const seenMessageIdsRef = useRef<Set<string>>(new Set())

  function markConsultMessageSeen(messageId: string | null | undefined) {
    const id = String(messageId ?? '').trim()
    if (!id) return
    seenMessageIdsRef.current.add(id)
    if (seenMessageIdsRef.current.size > 200) {
      seenMessageIdsRef.current = new Set([...seenMessageIdsRef.current].slice(-100))
    }
  }

  useEffect(() => {
    if (!hasToken) return undefined

    const pushUnsubscribe = ws.on<WsConsultMessage>('message_push', (m) => {
      if (!isConsultPath(location.pathname) && m.id) ackChatMessage(m.id)
      if (isOwnMessage(m, meId)) return

      const activeSessionId = getActiveSessionId(location.search)
      const isCurrentSession = isConsultPath(location.pathname) && activeSessionId === m.sessionId
      if (isCurrentSession) return

      const messageKey = getMessageDedupeKey(m)
      if (seenMessageIdsRef.current.has(messageKey)) return
      seenMessageIdsRef.current.add(messageKey)
      if (seenMessageIdsRef.current.size > 200) {
        seenMessageIdsRef.current = new Set([...seenMessageIdsRef.current].slice(-100))
      }

      const routeRole = resolveConsultRouteRole(location.pathname)
      const title = getNotificationTitle(location.pathname)
      const description = getMessagePreview(m)
      const target = buildConsultTarget(routeRole, m.sessionId)
      if (!isConsultPath(location.pathname)) {
        const updated = bumpConsultUnreadCache(queryClient, routeRole, m.sessionId, {
          preview: description,
        })
        if (!updated) {
          void queryClient.invalidateQueries({ queryKey: ['consult', 'sessions', routeRole] })
        }
      }

      addNotification({
        id: `consult:${m.id || `${m.sessionId}:${m.createTime ?? Date.now()}`}`,
        kind: 'consult',
        title,
        description,
        target,
        sessionId: m.sessionId,
        messageId: m.id,
        routeRole,
        createdAt: normalizeWsTimestamp(m.createTime),
      })
      showBrowserNotification(title, description, target)
    })

    const recallUnsubscribe = ws.on<WsMessageRecalledData>('message_recalled', (d) => {
      markConsultMessageSeen(d.messageId)
      const recalledUnreadCount = recallConsultNotification(d.messageId, {
        description: getRecallPreview(),
      })
      if (recalledUnreadCount <= 0) return

      const routeRole = resolveConsultRouteRole(location.pathname)
      decrementConsultUnreadCache(queryClient, routeRole, d.sessionId, {
        amount: recalledUnreadCount,
        preview: getRecallPreview(),
      })
      void queryClient.invalidateQueries({ queryKey: ['consult', 'sessions', routeRole] })
    })

    return () => {
      pushUnsubscribe()
      recallUnsubscribe()
    }
  }, [
    addNotification,
    hasToken,
    location.pathname,
    location.search,
    meId,
    queryClient,
    recallConsultNotification,
    ws,
  ])

  return null
}
